import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { DateTime } from "luxon";
import Select from "react-select";
import ConfirmPopup from "./ConfirmPopup";
import LoadingPopup from "../SharedView/LoadingPopup";
import InvoiceDrawer from "./InvoiceDrawer";
import "./invoicesManagement.css";
import { useZohoOrgId, zohoInvoiceUrl } from "../../utils/zohoOrg";

const money = (n) =>
  `AED ${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (iso) => {
  if (!iso) return "—";
  const dt = DateTime.fromISO(iso, { zone: "utc" }).setZone("Asia/Dubai");
  return dt.isValid ? dt.toFormat("dd LLL yyyy") : "—";
};

const PAGE_SIZE = 50;

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "draft", label: "Draft" },
  { value: "sent", label: "Sent" },
  { value: "overdue", label: "Overdue" },
  { value: "paid", label: "Paid" },
  { value: "partially_paid", label: "Partially Paid" },
  { value: "void", label: "Void" },
];

export default function InvoicesManagement() {
  const zohoOrgId = useZohoOrgId();
  const [invoices, setInvoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);

  // Filters
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(STATUS_OPTIONS[0]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [search, setSearch] = useState("");

  // Detail drawer — the panel itself is the shared <InvoiceDrawer>; this page
  // only tracks WHICH invoice is open. The drawer fetches + owns its actions.
  // /invoices?open=<id> lands with that invoice's drawer open — the wallet's
  // "awaiting Apply & Send" line links here (2026-09-22).
  const [searchParams] = useSearchParams();
  const [detailId, setDetailId] = useState(() => {
    const id = parseInt(searchParams.get('open'), 10);
    return Number.isFinite(id) ? id : null;
  });

  // Actions
  // Delete flow is a two-stage gate: 'confirm' (are you sure?) then 'type'
  // (literally type DELETE). `deleteTarget` holds the invoice across both.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteStage, setDeleteStage] = useState(null); // null | 'confirm' | 'type'
  const [deleteTyped, setDeleteTyped] = useState('');
  const deleteInputRef = useRef(null);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");

  // Invoice editing (delete here, and Send/Apply inside the drawer) is
  // MANAGERS ONLY (2026-07-20).
  const userRole = (localStorage.getItem('userRole') || '').toLowerCase();
  const isManager = userRole === 'manager';

  // Load clients
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get("/api/clients/active");
        setClients(res.data);
      } catch (e) { console.error(e); }
    })();
  }, []);

  const clientOptions = useMemo(
    () => clients.map(c => ({ value: c.id, label: c.display_name || `Client #${c.id}` })),
    [clients]
  );

  // `silent` refreshes the list WITHOUT the blanking loading state — used after
  // a drawer mutation (revert / apply / send) so the affected row updates in
  // place instead of the whole page appearing to reload (2026-07-21).
  const fetchInvoices = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const params = { offset, limit: PAGE_SIZE };
      if (selectedClient) params.clientId = selectedClient.value;
      if (selectedStatus?.value) params.status = selectedStatus.value;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (search) params.search = search;

      const { data } = await axios.get("/api/invoices/", { params });
      setInvoices(data.invoices || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error(e);
      if (!silent) { setInvoices([]); setTotal(0); }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [offset, selectedClient, selectedStatus, startDate, endDate, search]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  // When filters change (not offset), reset offset
  const resetAndFetch = () => { setOffset(0); };

  const requestDelete = (invoice) => {
    setDeleteTarget(invoice);
    setDeleteTyped('');
    setDeleteStage('confirm');
  };

  const cancelDelete = () => {
    setDeleteStage(null);
    setDeleteTarget(null);
    setDeleteTyped('');
  };

  // Auto-focus the DELETE input once the type-to-confirm stage opens.
  useEffect(() => {
    if (deleteStage === 'type') {
      const t = setTimeout(() => deleteInputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [deleteStage]);

  const doDelete = async (force = false) => {
    if (!deleteTarget) return;
    const id = deleteTarget.id;
    setDeleteStage(null);
    setDeleteTarget(null);
    setDeleteTyped('');
    setIsActionLoading(true);
    setFeedbackMessage("");
    try {
      const { data } = await axios.delete(`/api/invoices/${id}`, { data: { force } });
      setFeedbackMessage(`Deleted. ${data.bookings_unlinked || 0} booking(s) unlinked.`);
      if (detailId === id) setDetailId(null);
      fetchInvoices();
    } catch (e) {
      console.error(e);
      setFeedbackMessage(e.response?.data?.message || "Failed to delete invoice.");
    } finally {
      setIsActionLoading(false);
    }
  };

  const [syncOpen, setSyncOpen] = useState(false);
  const [syncRange, setSyncRange] = useState("90d");

  const computeSyncDates = (range) => {
    const now = DateTime.now().setZone("Asia/Dubai");
    if (range === "90d") return { startDate: now.minus({ days: 90 }).toFormat("yyyy-LL-dd"), endDate: now.toFormat("yyyy-LL-dd") };
    if (range === "365d") return { startDate: now.minus({ days: 365 }).toFormat("yyyy-LL-dd"), endDate: now.toFormat("yyyy-LL-dd") };
    return {}; // all
  };

  const doSync = async () => {
    setSyncOpen(false);
    setIsActionLoading(true);
    setFeedbackMessage("Syncing with Zoho — this may take a moment...");
    try {
      const dates = computeSyncDates(syncRange);
      const { data } = await axios.post("/api/invoices/sync", dates);
      setFeedbackMessage(
        data.deleted > 0
          ? `Sync complete. Checked ${data.checked} invoices, deleted ${data.deleted} that no longer exist in Zoho. (${data.api_calls} API calls)`
          : `Sync complete. All ${data.checked} invoices are in sync with Zoho. (${data.api_calls} API calls)`
      );
      fetchInvoices();
    } catch (e) {
      console.error(e);
      setFeedbackMessage(e.response?.data?.message || "Failed to sync with Zoho.");
    } finally {
      setIsActionLoading(false);
    }
  };

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="invMgr">
      <div className="invMgr__header">
        <h1 className="invMgr__title">Invoices</h1>
        <button className="invMgr__btn invMgr__btn--ghost" onClick={() => setSyncOpen(true)}>
          Sync with Zoho
        </button>
      </div>

      {/* Filters */}
      <div className="invMgr__filters">
        <div className="invMgr__filter">
          <label>Client</label>
          <Select
            classNamePrefix="inv-sel"
            value={selectedClient}
            onChange={(v) => { setSelectedClient(v); resetAndFetch(); }}
            options={clientOptions}
            isClearable isSearchable
            placeholder="All Clients"
            menuPortalTarget={document.body} menuPosition="fixed"
          />
        </div>
        <div className="invMgr__filter">
          <label>Status</label>
          <Select
            classNamePrefix="inv-sel"
            value={selectedStatus}
            onChange={(v) => { setSelectedStatus(v); resetAndFetch(); }}
            options={STATUS_OPTIONS}
            isSearchable={false}
            menuPortalTarget={document.body} menuPosition="fixed"
          />
        </div>
        <div className="invMgr__filter">
          <label>From</label>
          <input type="date" className="invMgr__input" value={startDate} onChange={(e) => { setStartDate(e.target.value); resetAndFetch(); }} />
        </div>
        <div className="invMgr__filter">
          <label>To</label>
          <input type="date" className="invMgr__input" value={endDate} onChange={(e) => { setEndDate(e.target.value); resetAndFetch(); }} />
        </div>
        <div className="invMgr__filter invMgr__filter--wide">
          <label>Search</label>
          <input type="text" className="invMgr__input" placeholder="Invoice number or client name" value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') resetAndFetch(); }} />
        </div>
      </div>

      <div className="invMgr__stats">
        <span>{total} invoice{total === 1 ? '' : 's'}</span>
        <span className="invMgr__page">Page {page} of {totalPages}</span>
      </div>

      {/* Table */}
      {loading ? (
        <div className="invMgr__loading">
          <div className="app-loading"><div className="app-spinner" /><div className="app-spinner-text">Loading invoices...</div></div>
        </div>
      ) : invoices.length === 0 ? (
        <div className="invMgr__empty">
          <div className="invMgr__emptyTitle">No invoices found</div>
          <div className="invMgr__emptyText">Try different filters.</div>
        </div>
      ) : (
        <div className="invMgr__tableWrap">
          <table className="invMgr__table">
            <thead>
              <tr>
                <th>Number</th>
                <th>Client</th>
                <th>Created</th>
                <th>Due</th>
                <th className="invMgr__right">Total</th>
                <th className="invMgr__right">Paid</th>
                <th className="invMgr__right">Balance</th>
                <th>Bookings</th>
                <th>Status</th>
                <th className="invMgr__right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => {
                const status = (inv.status || '').toLowerCase();
                return (
                  <tr key={inv.id} className="invMgr__tr" onClick={() => setDetailId(inv.id)}>
                    <td>
                      {inv.zoho_invoice_id && zohoOrgId ? (
                        <a href={zohoInvoiceUrl(zohoOrgId, inv.zoho_invoice_id)}
                          target="_blank" rel="noopener noreferrer" className="invMgr__link"
                          onClick={(e) => e.stopPropagation()}>
                          {inv.zoho_invoice_number || `#${inv.id}`}
                        </a>
                      ) : (
                        <span>{inv.zoho_invoice_number || `#${inv.id}`}</span>
                      )}
                    </td>
                    {/* Client + billing entity in ONE compact cell (2026-08-09:
                        multi-billing applies to a handful of clients — no column).
                        Truncated with ellipsis; full names on hover. */}
                    <td className="invMgr__clientCell"
                        title={inv.billing_entity_label
                          ? `${inv.client_name || `Client #${inv.client_id}`} — ${inv.billing_entity_label}`
                          : (inv.client_name || `Client #${inv.client_id}`)}>
                      <span className="invMgr__clientName">{inv.client_name || `Client #${inv.client_id}`}</span>
                      {inv.billing_entity_label && (
                        <span className="invMgr__clientEntity">{inv.billing_entity_label}</span>
                      )}
                    </td>
                    <td>{fmtDate(inv.created_at)}</td>
                    <td>{fmtDate(inv.due_date)}</td>
                    <td className="invMgr__right invMgr__bold">{money(inv.total_amount)}</td>
                    <td className="invMgr__right">{inv.amount_paid == null ? '—' : money(inv.amount_paid)}</td>
                    <td className="invMgr__right">{inv.balance == null ? '—' : money(inv.balance)}</td>
                    <td>{inv.booking_count}</td>
                    <td>
                      <span className={`invMgr__statusChip invMgr__statusChip--${status.replace(/\s+/g, '-')}`}>
                        {(inv.status || 'draft').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                      </span>
                    </td>
                    <td className="invMgr__right" onClick={(e) => e.stopPropagation()}>
                      <button className="invMgr__btn invMgr__btn--ghost" onClick={() => setDetailId(inv.id)}>View</button>
                      {isManager && (
                        <button className="invMgr__btn invMgr__btn--danger" onClick={() => requestDelete(inv)}>Delete</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="invMgr__pagination">
          <button className="invMgr__btn invMgr__btn--ghost" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Prev</button>
          <span className="invMgr__pageInfo">Page {page} of {totalPages}</span>
          <button className="invMgr__btn invMgr__btn--ghost" disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>Next</button>
        </div>
      )}

      {/* Detail drawer — the shared <InvoiceDrawer> (same component the
          booking-summary page uses). It fetches its own detail and owns the
          unified Send / Apply Payment & Send, revert and delete actions;
          this page only says which invoice is open and refreshes on change.
          Edit actions are MANAGERS ONLY — supervisors get a read-only drawer. */}
      <InvoiceDrawer
        open={!!detailId}
        invoiceId={detailId}
        canEdit={userRole === 'manager'}
        onClose={() => setDetailId(null)}
        // Revert / apply / send: refresh in the background (no blanking reload) —
        // only the affected row visibly changes. Delete: drop that row in place.
        onChanged={() => fetchInvoices({ silent: true })}
        onDeleted={(_bookingIds, deletedId) => {
          // deletedId is the drawer's CURRENT invoice — after a regenerate it
          // differs from detailId (audit 2026-08-09).
          const gone = deletedId || detailId;
          setInvoices((prev) => prev.filter((inv) => inv.id !== gone));
          setTotal((t) => Math.max(0, t - 1));
        }}
      />

      <LoadingPopup isLoading={isActionLoading} feedbackMessage={feedbackMessage} onClose={() => setFeedbackMessage("")} />

      {/* Step 1 — plain "Are you sure?" */}
      <ConfirmPopup
        isOpen={deleteStage === 'confirm'}
        title="Delete Invoice"
        message={deleteTarget
          ? `Are you sure you want to delete invoice ${deleteTarget.zoho_invoice_number || `#${deleteTarget.id}`}? This will also delete it from Zoho (if it still exists) and unlink ${deleteTarget.booking_count || 0} booking(s). This action cannot be undone.`
          : ''}
        confirmLabel="Yes, continue"
        cancelLabel="Cancel"
        isDestructive={true}
        onConfirm={() => {
          setDeleteTyped('');
          setDeleteStage('type');
        }}
        onCancel={cancelDelete}
      />

      {/* Step 2 — user must literally type DELETE */}
      <ConfirmPopup
        isOpen={deleteStage === 'type'}
        title="Type DELETE to confirm"
        message={
          <div>
            <p style={{ marginTop: 0 }}>
              This really can't be undone. To confirm deletion of invoice{' '}
              <strong>
                {deleteTarget?.zoho_invoice_number || `#${deleteTarget?.id || ''}`}
              </strong>
              , type <strong>DELETE</strong> below.
            </p>
            <input
              ref={deleteInputRef}
              type="text"
              value={deleteTyped}
              onChange={(e) => setDeleteTyped(e.target.value)}
              placeholder="Type DELETE"
              autoComplete="off"
              spellCheck={false}
              style={{
                width: '100%',
                padding: '8px 10px',
                fontSize: 14,
                border: '1px solid #ccc',
                borderRadius: 4,
                boxSizing: 'border-box',
                fontFamily: 'inherit',
                letterSpacing: 0.5,
              }}
            />
          </div>
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isDestructive={true}
        confirmDisabled={deleteTyped !== 'DELETE'}
        onConfirm={() => doDelete(false)}
        onCancel={cancelDelete}
      />

      {/* Apply / Revert confirms moved INTO <InvoiceDrawer> — they belong with
          the drawer's own actions now (owner 2026-07-20 unification). */}

      {syncOpen && (
        <div className="invMgr__drawerOverlay" onClick={() => setSyncOpen(false)}>
          <div className="invMgr__syncModal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-x" onClick={() => setSyncOpen(false)} />
            <h2 className="invMgr__syncTitle">Sync with Zoho</h2>
            <p className="invMgr__syncDesc">
              Choose how far back to check. Smaller ranges = much faster. Any invoice in the selected range that no longer exists in Zoho will be deleted locally.
            </p>
            <div className="invMgr__rangePills">
              {[
                { v: "90d", l: "Last 90 days" },
                { v: "365d", l: "Last 365 days" },
                { v: "all", l: "All time" },
              ].map(opt => (
                <button
                  key={opt.v}
                  type="button"
                  className={`invMgr__rangePill ${syncRange === opt.v ? 'is-active' : ''}`}
                  onClick={() => setSyncRange(opt.v)}
                >
                  {opt.l}
                </button>
              ))}
            </div>
            <div className="invMgr__syncActions">
              <button className="invMgr__btn invMgr__btn--ghost" onClick={() => setSyncOpen(false)}>Cancel</button>
              <button className="invMgr__btn invMgr__btn--primary" onClick={doSync}>Sync Now</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
